import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiEndpoints } from "../../services/apiConfig";
import axios from "axios";
import { clearCampaignDraft } from "../../pages/sendEmail/CreateCampaignStepper";

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(apiEndpoints.login, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return data;
    } catch (error) {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "Login failed.";

      return rejectWithValue(message);
    }
  }
);

export const signUpUser = createAsyncThunk(
  "auth/signUpUser",
  async (payload, { rejectWithValue }) => {
    try {
      const data = await axios.post(apiEndpoints.signUp, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Signup failed."
      );
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const data = await axios.post(apiEndpoints.verifyOtp, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "OTP verification failed."
      );
    }
  }
);

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const data = await axios.post(apiEndpoints.resendOtp, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to resend OTP."
      );
    }
  }
);

export const logoutUser = createAsyncThunk("auth/logoutUser", async () => {
  try {
    clearCampaignDraft();
    localStorage.clear();
    localStorage.removeItem("filters");
    return true;
  } catch (error) {
    console.error("Logout error:", error);
    throw error;
  }
});

export const validateToken = createAsyncThunk(
  "auth/validateToken",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        return rejectWithValue("No token found");
      }

      const response = await axios.get(apiEndpoints.getuserProfile, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        token,
        user: response.data.data,
        userType: localStorage.getItem("userType"),
        userId: localStorage.getItem("userId"),
      };
    } catch (error) {
      localStorage.clear();
      return rejectWithValue("Token validation failed");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    loading: false,
    error: null,
    userData: null,
    resendLoading: false,
    message: "",
    isAuthenticated: false,
    userId: localStorage.getItem("userId") || null,
    token: localStorage.getItem("token") || null,
    userType: localStorage.getItem("userType") || null,
    tokenValidated: false,
    verifyOtpResponse: null,
    resendOtpResponse: null,
    otpVerifyStatus: null, // Track OTP verification status
    pendingUserEmail: null, // Store email for pending OTP verification
  },
  reducers: {
    markTokenValidated: (state) => {
      state.tokenValidated = true;
      state.loading = false;
    },
    clearPendingVerification: (state) => {
      state.pendingUserEmail = null;
      state.otpVerifyStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
        state.otpVerifyStatus = action.payload.otp_verify_status;

        // Store user data regardless of authentication status

        // Check if user is fully authenticated
        if (
          action.payload.access_token &&
          action.payload.otp_verify_status === true
        ) {
          state.isAuthenticated = true;
          state.token = action.payload.access_token;
          state.userData = action.payload.user;

          // Store in localStorage
          localStorage.setItem("token", action.payload.access_token);

          if (action.payload.user.user_type) {
            state.userType = action.payload.user.user_type;
            localStorage.setItem(
              "userType",
              JSON.stringify(action.payload.user.user_type)
            );
          }
        } else if (
          !action.payload.access_token &&
          action.payload.otp_verify_status === false
        ) {
          // OTP verification required - not authenticated yet
          state.isAuthenticated = false;
          state.token = null;
          state.pendingUserEmail = action.payload.user.email;
        } else {
          // Handle other cases
          state.isAuthenticated = false;
          state.token = null;
        }

        state.error = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.otpVerifyStatus = null;
      })

      // Signup cases
      .addCase(signUpUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signUpUser.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
        state.otpVerifyStatus = action.payload.otp_verify_status;

        // Store user email for OTP verification if needed
        if (action.payload.user?.email && !action.payload.otp_verify_status) {
          state.pendingUserEmail = action.payload.user.email;
        }
      })
      .addCase(signUpUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Verify OTP cases
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.verifyOtpResponse = action.payload;
        state.otpVerifyStatus = action.payload.success ? true : false;

        // Clear pending verification on success
        if (action.payload.success) {
          state.pendingUserEmail = null;
        }
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Resend OTP cases
      .addCase(resendOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.resendOtpResponse = action.payload;
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(logoutUser.pending, (state) => {
        state.loading = null;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.userData = null;
        state.token = null;
        state.isAuthenticated = false;
        state.userId = null;
        state.userType = null;
        state.tokenValidated = false;
        state.otpVerifyStatus = null;
        state.pendingUserEmail = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })

      // Token validation cases
      .addCase(validateToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.tokenValidated = true;
        state.token = action.payload.token;
        state.userData = action.payload.user;
        state.userId = action.payload.userId;
        state.userType = action.payload.userType;
      })
      .addCase(validateToken.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.tokenValidated = true;
        state.token = null;
        state.userData = null;
        state.userId = null;
        state.userType = null;
        state.otpVerifyStatus = null;
        state.pendingUserEmail = null;
      });
  },
});

export const { markTokenValidated, clearPendingVerification } =
  authSlice.actions;
export default authSlice.reducer;
